import { AxiosPromise } from 'axios';

import { mmHttp, MmRequestConfig } from './mmHttp';
import {
  buildAssignmentsFilterBody,
  MmWireAuditorId,
  readAssignmentsPage,
  readAssignmentsSummary,
} from './assignmentsWire';
import {
  MmAssignmentImportPreview,
  MmAssignmentImportResult,
  MmAssignmentListFilters,
  MmAssignmentsSummary,
  MmImportErrorDetails,
  MmImportFailure,
  MmPage,
} from '../types/dto';
import { MmStoreAuditorAssignment } from '../types/domain';

const ASSIGNMENTS_PATH = '/auditor-assignments';

const IMPORT_SEGMENT = '/import';

/** null — снять аудитора; тип не сужать до числа: с бэкенда приходит UUID */
export interface MmAssignmentPair {
  competitorCode: string;
  auditorId: MmWireAuditorId;
}

export function buildAssignmentPair(
  row: MmStoreAuditorAssignment,
  auditorId: MmWireAuditorId,
): MmAssignmentPair {
  return {
    competitorCode: row.competitorCode,
    auditorId: auditorId,
  };
}

export function fetchAssignments(
  tradingNetworkId: number,
  filters: Partial<MmAssignmentListFilters>,
  pageParams: { [key: string]: any },
  config?: MmRequestConfig,
): Promise<MmPage<MmStoreAuditorAssignment>> {
  return mmHttp
    .post(
      ASSIGNMENTS_PATH + '/search',
      buildAssignmentsFilterBody(tradingNetworkId, filters),
      Object.assign({ params: pageParams, mmSection: 'assignments/list' }, config),
    )
    .then((response) => readAssignmentsPage(response.data));
}

/** Фильтр по аудитору вызывающий вырезает сам: иначе счётчик покажет «N из N» */
export function fetchAssignmentsSummary(
  tradingNetworkId: number,
  filters: Partial<MmAssignmentListFilters>,
  config?: MmRequestConfig,
): AxiosPromise<MmAssignmentsSummary> {
  return mmHttp
    .post(
      ASSIGNMENTS_PATH + '/summary',
      buildAssignmentsFilterBody(tradingNetworkId, filters),
      Object.assign({ mmSection: 'assignments/summary' }, config),
    )
    .then((response) => {
      response.data = readAssignmentsSummary(response.data);

      return response;
    });
}

export function saveAssignments(
  tradingNetworkId: number,
  assignments: MmAssignmentPair[],
  config?: MmRequestConfig,
): AxiosPromise<{ updated: number }> {
  return mmHttp.put(
    ASSIGNMENTS_PATH,
    { tradingNetworkId: tradingNetworkId, assignments: assignments },
    Object.assign({ mmSection: 'assignments/save' }, config),
  );
}

/** Имя файла берём из Content-Disposition, расширение не зашито */
export function exportAssignments(
  tradingNetworkId: number,
  filters: Partial<MmAssignmentListFilters>,
  config?: MmRequestConfig,
): AxiosPromise<Blob> {
  return mmHttp.post(
    ASSIGNMENTS_PATH + '/export',
    buildAssignmentsFilterBody(tradingNetworkId, filters),
    Object.assign({ responseType: 'blob', mmSection: 'assignments/export' }, config),
  );
}

/** Content-Type не задаём руками: браузер проставит его вместе с boundary */
function buildImportFormData(file: File): FormData {
  const formData = new FormData();

  formData.append('file', file);

  return formData;
}

const IMPORT_DETAILS_FIELDS = [
  'unknownCompetitorCodes',
  'unknownAuditorLogins',
  'duplicateCompetitorCodes',
];

/** Пустая раскладка вместо `null` заставила бы экран предложить показ кодов, которых нет */
function readImportErrorDetails(raw: any): MmImportErrorDetails | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const normalized: { [key: string]: string[] } = {};
  let hasAny = false;

  for (let i = 0; i < IMPORT_DETAILS_FIELDS.length; i += 1) {
    const field = IMPORT_DETAILS_FIELDS[i];
    const candidate = raw[field];

    if (Array.isArray(candidate)) {
      hasAny = true;
      normalized[field] = candidate.map((item: any) => String(item));
    } else {
      normalized[field] = [];
    }
  }

  if (!hasAny) {
    return null;
  }

  return {
    unknownCompetitorCodes: normalized.unknownCompetitorCodes,
    unknownAuditorLogins: normalized.unknownAuditorLogins,
    duplicateCompetitorCodes: normalized.duplicateCompetitorCodes,
  };
}

export function importAssignments(
  tradingNetworkId: number,
  file: File,
  config?: MmRequestConfig,
): AxiosPromise<MmAssignmentImportResult> {
  return mmHttp.post(
    ASSIGNMENTS_PATH + IMPORT_SEGMENT,
    buildImportFormData(file),
    Object.assign(
      { params: { tradingNetworkId: tradingNetworkId }, mmSection: 'assignments/import' },
      config,
    ),
  );
}

export function dryRunImportAssignments(
  tradingNetworkId: number,
  file: File,
  config?: MmRequestConfig,
): AxiosPromise<MmAssignmentImportPreview> {
  return mmHttp.post(
    ASSIGNMENTS_PATH + IMPORT_SEGMENT,
    buildImportFormData(file),
    Object.assign(
      {
        params: { tradingNetworkId: tradingNetworkId, dryRun: true },
        mmSection: 'assignments/import-dry-run',
      },
      config,
    ),
  );
}

const IMPORT_FAILURE_CODE = 'import_validation_failed';

function readImportErrorLines(details: any): string {
  if (!details || !Array.isArray(details.lines)) {
    return '';
  }

  const lines: string[] = [];

  for (let i = 0; i < details.lines.length; i += 1) {
    lines.push(String(details.lines[i]));
  }

  return lines.join('\n');
}

export function readImportFailure(error: any): MmImportFailure | null {
  const data = error && error.response ? error.response.data : null;

  if (!data || typeof data !== 'object') {
    return null;
  }

  if (data.code !== IMPORT_FAILURE_CODE) {
    return null;
  }

  const details = data.details && typeof data.details === 'object' ? data.details : null;

  return {
    message: typeof data.message === 'string' ? data.message : '',
    errorsText: readImportErrorLines(details),
    details: readImportErrorDetails(details),
  };
}
